import { cn } from '@/lib/utils'

/**
 * The Welcome Quick Start Guide content, shared between the public intake
 * page (compact, above the form) and the authed /getting-started page (the
 * full guide). Copy mirrors the welcome-packet email and the printed guide;
 * the contact directory mirrors the API's CONTACT_DIRECTORY export
 * (greenline-hub-api lib/email/templates/welcome-packet.ts) — same four
 * addresses, same labels.
 */

/** Who to write, from day one — keep in lockstep with the email template. */
const CONTACT_DIRECTORY = [
  { label: 'Sales & pricing', email: 'sales@greenlineingredients.com' },
  { label: 'Orders & documents', email: 'orders@greenlineingredients.com' },
  { label: 'Shipping & logistics', email: 'shipping@greenlineingredients.com' },
  { label: 'Samples', email: 'samples@greenlineingredients.com' },
] as const

/** The six-step working relationship, Onboarding through Ongoing Support. */
const PROCESS_STEPS = [
  { name: 'Onboarding', description: 'Account setup and your customer intake.' },
  { name: 'Evaluation', description: 'Samples and technical evaluation for your application.' },
  { name: 'Ordering', description: 'Purchase orders in — confirmations back within 48 hours.' },
  {
    name: 'Production or Allocation',
    description: 'Stock is allocated, or your run goes on the production schedule.',
  },
  { name: 'Shipping', description: 'Shipment, tracking, and invoicing.' },
  { name: 'Ongoing Support', description: 'Reorders, forecasting, and documentation.' },
] as const

/** The welcome lead — thanks first, then what Green Line is. */
export function WelcomeIntro({ className }: { className?: string }) {
  return (
    <p className={cn('text-sm leading-relaxed text-inkMid', className)}>
      Thank you for choosing Green Line Ingredients as your ingredients partner. We supply
      specialty ingredients from Hazlet, NJ — and from first sample to standing reorder, this is
      how we&rsquo;ll work together.
    </p>
  )
}

/**
 * The six process steps. `compact` is the one-line-per-step strip for the
 * intake page; `full` gives each step room on the getting-started guide.
 */
export function ProcessSteps({
  variant = 'full',
  className,
}: {
  variant?: 'compact' | 'full'
  className?: string
}) {
  if (variant === 'compact') {
    return (
      <ol className={cn('space-y-2', className)}>
        {PROCESS_STEPS.map((step, index) => (
          <li key={step.name} className="flex items-baseline gap-3">
            <span aria-hidden="true" className="w-4 shrink-0 font-mono text-[0.6875rem] text-mute">
              {index + 1}
            </span>
            <p className="text-sm leading-snug">
              <span className="font-medium text-ink">{step.name}</span>{' '}
              <span className="text-mute">— {step.description}</span>
            </p>
          </li>
        ))}
      </ol>
    )
  }

  return (
    <ol className={cn('divide-y divide-rule/40', className)}>
      {PROCESS_STEPS.map((step, index) => (
        <li key={step.name} className="flex items-baseline gap-4 py-3 first:pt-0 last:pb-0">
          <span aria-hidden="true" className="w-6 shrink-0 font-mono text-xs text-mute">
            {String(index + 1).padStart(2, '0')}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">{step.name}</p>
            <p className="mt-0.5 text-sm leading-relaxed text-mute">{step.description}</p>
          </div>
        </li>
      ))}
    </ol>
  )
}

/** The mono directory strip — who to write, from day one. */
export function ContactDirectory({ className }: { className?: string }) {
  return (
    <ul className={cn('overflow-hidden rounded-md border border-rule bg-paper', className)}>
      {CONTACT_DIRECTORY.map((entry) => (
        <li
          key={entry.email}
          className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 border-b border-rule/40 px-4 py-2.5 last:border-b-0"
        >
          <span className="text-sm text-inkMid">{entry.label}</span>
          <a
            href={`mailto:${entry.email}`}
            className="rounded-sm font-mono text-[0.8125rem] text-brand underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:underline"
          >
            {entry.email}
          </a>
        </li>
      ))}
    </ul>
  )
}
